import { StepHandler } from './step-handler';
import { PostalCodeValidator } from './postal-code-validator';
import { ContactFormHandler } from './contact-form-handler';
import { analyticsManager } from './../analytics-utils.js';
export class ServiceGuide {
    constructor(element, options) {
        this.options = options;
        this.priorityOrder = options.priorityOrder;
        this.informationQuestions = options.informationQuestions;
        this.pageId = options.pageId;
        this.serviceoptionNotSelectedId = options.serviceoptionNotSelectedId;

        // Dependencies
        this.postalValidator = new PostalCodeValidator();
        this.contactHandler = new ContactFormHandler();

        this.stepHandler = new StepHandler(
            // Step change callback
            (currentStep, totalSteps) => {
                if (currentStep === totalSteps  + 1 ) {
                    this.showServiceDetails();
                }
            },
            // Validate callback
            () => this.validateCurrentStep(),
            // Next button text
            options.nextButtonText,
            // Recommendation button text
            options.recommendationButtonText,
            // Total steps
            options.informationQuestionCount + 2 // +1 for postal code, +1 for service selection
        );

        // State
        this.formData = {
            answers: new Array(options.informationQuestionCount).fill(null),
            priorityList: [],
            servicesClicked: [],
            postalCode: null,
            selectedService: null,
            phoneOrEmail: null,
            newsletter: false,
            terms: false,
            pageId: options.pageId,
            gRecaptchaResponse: null
        };

        this.modal = element;
        this.lastFocusedElement = null;

        this.initializeElements();
        this.initializeContactForm();
        this.attachEventListeners();
    }

    getValidServices(postalCode) {
        const postalNumber = parseInt(postalCode);

        return this.options.serviceOptions.filter(service => {
            // Service er gyldig hvis den har tomme postnummer-lister
            if (!service.postalCodes?.length && !service.ranges?.length) {
                return true;
            }
            
            //sjekk specific postal codes
            const validPostalCodes = service.postalCodes?.some(code => {
                const codeNumber = parseInt(code);
                return postalNumber === codeNumber;
            });
            
            if (validPostalCodes) {
                return true;
            }
            
            // Sjekk ranges
            return service.ranges?.some(range => {
                const startNumber = parseInt(range.start);
                const endNumber = parseInt(range.end);
                return postalNumber >= startNumber && postalNumber <= endNumber;
            });
        });
    }

    handlePostalCode(postalCode) {
        if (!this.postalValidator.validate(postalCode)) {
            return;
        }

        this.formData.postalCode = postalCode;
        const validServices = this.getValidServices(postalCode);

        if (validServices.length > 0) {
            const container = this.modal.querySelector('.service-options-container');

            // Finn alle option-buttons og sjekk deres data-service-id mot validServices
            container.querySelectorAll('.service-option').forEach(button => {
                const buttonServiceId = button.dataset.serviceId?.trim(); // Fjern eventuelle mellomrom
                const service = validServices.find(s => s.id.trim() === buttonServiceId); // Sammenlign med trimmed id

                if (service) {
                    button.style.display = 'block'; // Vis knappen hvis det er en match
                } else {
                    button.style.display = 'none'; // Skjul knappen ellers
                }
            });
        }
    }

    validateCurrentStep() {
        const stepIndex = this.stepHandler.getCurrentStep() - 1;

        this.hideError();
        
        // Validering av spørsmål
        if (stepIndex < this.formData.answers.length) {
            //also check if the answer is not null and that the answer is not an empty array
            if (this.formData.answers[stepIndex] === null || this.formData.answers[stepIndex].length === 0) {
                this.showError(this.modal.dataset.errorNotAnswered);
                return false;
            }
        }

        // Validering av postnummer
        if (stepIndex === this.formData.answers.length) {
            return this.postalValidator.validate(this.formData.postalCode);
        }
        
        // Validering av service valg
        if (stepIndex === this.formData.answers.length + 1) {
            const selectedServices = this.formData.servicesClicked;
            
            // Hvis ingen tjenester er valgt, vis feilmelding
            if (selectedServices.length === 0) {
                this.showError(this.modal.dataset.errorNotAnswered);
                return false;
            }
            
            const priorityOrder = this.priorityOrder;
            
            //if selectedservices firsty entry is unknowm display the highest priority service
            if (selectedServices.length === 1 && selectedServices[0] === 'unknown') {
                this.formData.selectedService = parseInt(priorityOrder[0], 10);
                return true;
            }

            // Finn den tjenesten med høyest prioritet
            const highestPriority = priorityOrder.find(priority =>
                selectedServices.includes(parseInt(priority, 10))
            );

            // Valider og sett selectedService
            this.formData.selectedService = highestPriority !== undefined
                ? selectedServices.find(service => parseInt(service, 10) === highestPriority)
                : null;
            
            return true;
        }


        // Validering av kontaktinfo
        if (stepIndex === this.formData.answers.length + 2) {
            const validationResult = this.contactHandler.validate();
            if (validationResult === true) {
                const formData = this.contactHandler.getFormData();
                this.formData.phoneOrEmail = formData.phoneOrEmailInput;
                this.formData.newsletter = formData.newsletter;
                this.formData.terms = formData.terms;
                return true;
            }
            return false;
        }

        return true;
    }
    
    showError(message) {
        if (this.errorMessage) {
            this.errorMessage.textContent = message;
            this.errorMessage.style.visibility = 'visible';
            this.errorMessage.setAttribute('role', 'alert');
        }
    }

    hideError() {
        if (this.errorMessage) {
            this.errorMessage.style.visibility = 'hidden';
            this.errorMessage.removeAttribute('role');
        }
    }
    
    initializeElements() {
        this.trigger = document.querySelector('[data-modal-trigger="service-guide"]');
        this.closeButtons = this.modal.querySelectorAll('.modal-close');
        this.stepPanels = this.modal.querySelectorAll('.step-panel');
        this.prevButton = this.modal.querySelector('.nav-button.prev');
        this.nextButton = this.modal.querySelector('.nav-button.next');
        this.modalContent = this.modal.querySelector('.modal-content');
        this.postalInput = this.modal.querySelector('#postal-code');
        this.progressIndicator = this.modal.querySelector('.progress-indicator');
        this.errorMessage = this.modal.querySelector('.error-message');
    }

    initializeContactForm() {
        this.phoneOrEmailInput = this.modal.querySelector('#phoneoremail');
        this.newsletterCheckbox = this.modal.querySelector('#newsletter');
        this.termsCheckbox = this.modal.querySelector('#terms');

        this.phoneOrEmailInput?.addEventListener('input', (e) => {
            this.formData.phoneOrEmail = e.target.value;
        });

        this.newsletterCheckbox?.addEventListener('change', (e) => {
            this.formData.newsletter = e.target.checked;
        });

        this.termsCheckbox?.addEventListener('change', (e) => {
            this.formData.terms = e.target.checked;
        });
    }

    attachEventListeners() {
        // Modal trigger
        this.trigger?.addEventListener('click', () => this.openModal());

        // Close button and overlay click
        this.closeButtons?.forEach(button => {
            const shouldReset = button.dataset.resetform === 'true';
            button.addEventListener('click', () => this.closeModal(shouldReset));
        });
        
        this.modal?.addEventListener('click', (e) => {
            if (e.target === this.modal) this.closeModal();
        });
        
        //Postal code input
        this.postalInput?.addEventListener('input', (e) => {
            const value = e.target.value;

            // Only allow numbers
            if (!/^\d*$/.test(value)) {
                e.target.value = value.replace(/\D/g, '');
                return;
            }

            if (this.postalValidator.validate(value)) {
                this.handlePostalCode(value);
            }
        });

        // Keyboard handling
        document.addEventListener('keydown', (e) => {
            if (!this.modal.classList.contains('visible')) return;

            if (e.key === 'Escape') {
                this.closeModal();
            } else if (e.key === 'Enter' && document.activeElement.classList.contains('option-button')) {
                document.activeElement.click();
            }
        });

        // Option buttons
        this.modal.querySelectorAll('.option-button').forEach(button => {
            button.addEventListener('click', (e) => this.handleOptionSelection(e));
        });

        this.modal.querySelectorAll('.service-option').forEach(button => {
            button.addEventListener('click', (e) => this.handleServiceSelection(e));
        });

        this.modal.querySelectorAll('.service-guide-submit').forEach(button => {
            button.addEventListener('click', (e) => this.submitForm());
        });

        // Navigation buttons
        this.prevButton?.addEventListener('click', () => this.stepHandler.navigatePrevious());
        this.nextButton?.addEventListener('click', () => this.stepHandler.navigateNext());
        this.modal.addEventListener('keydown', (e) => this.handleTabKey(e));
    }

    handleServiceSelection(e) {
        const button = e.target;
        const isUnknownOption = button.dataset.value === 'unknown';
        const parsedToInt = parseInt(button.dataset.epiContentid);

        if (isUnknownOption) {
            // Fjern alle andre valgte tjenester
            this.formData.servicesClicked = [];
            this.modal.querySelectorAll('.service-option').forEach(button => {
                this.removeButtonSelectedClasses(button);
            });
            this.addButtonSelectedClasses(button);
            this.formData.servicesClicked.push(this.serviceoptionNotSelectedId);
            return;
        }

        // Hvis en vanlig service velges, fjern 'unknown' hvis den eksisterer
        const unknownButton = this.modal.querySelector('.service-option[data-value="unknown"]');
        if (unknownButton && unknownButton.classList.contains('selected')) {
            this.removeButtonSelectedClasses(unknownButton);
            this.formData.servicesClicked = this.formData.servicesClicked.filter(
                id => id !== this.serviceoptionNotSelectedId
            );
        }

        const index = this.formData.servicesClicked.indexOf(parsedToInt);

        if (index === -1) {
            // Hvis ID ikke finnes i listen, legg den til og marker knappen som valgt
            this.addButtonSelectedClasses(button);
            this.formData.servicesClicked.push(parsedToInt);
        } else {
            // Hvis ID allerede finnes i listen, fjern den og marker knappen som ikke-valgt
            this.removeButtonSelectedClasses(button);
            this.formData.servicesClicked.splice(index, 1);
        }
    }
    
    handleOptionSelection(e) {
        const button = e.target;
        const stepPanel = button.closest('.step-panel');

        if (!stepPanel) {
            console.error("Step panel not found");
            return;
        }

        const stepIndex = parseInt(stepPanel.dataset.step) - 1;
        const isStepMultipleChoice = stepPanel.getAttribute('data-multiple-choice') === 'True';
        const isUnknownOption = button.dataset.value === 'unknown';

        // Initialiser formData hvis den ikke eksisterer
        if (!this.formData) {
            this.formData = { answers: [] };
        }
        if (!Array.isArray(this.formData.answers)) {
            this.formData.answers = [];
        }

        // For vanlige alternativer, sjekk dataset attributter
        if (!isUnknownOption && (!button.dataset || !button.dataset.questionId || !button.dataset.value)) {
            console.error("Button or required dataset attributes are missing");
            return;
        }

        if (isStepMultipleChoice) {
            let currentAnswers = this.formData.answers[stepIndex] || [];

            if (isUnknownOption) {
                // Hvis "unknown" velges, fjern alle andre valg
                stepPanel.querySelectorAll('.option-button').forEach(btn => {
                    this.removeButtonSelectedClasses(btn);
                });
                this.addButtonSelectedClasses(button);
                this.formData.answers[stepIndex] = ['unknown'];
            } else {
                // Hvis et annet alternativ velges, fjern "unknown" hvis det er valgt
                const unknownButton = stepPanel.querySelector('.option-button[data-value="unknown"]');
                if (unknownButton && unknownButton.getAttribute('aria-checked') === 'true') {
                    this.removeButtonSelectedClasses(unknownButton);
                    currentAnswers = []; // Tøm listen siden "unknown" var valgt
                }

                const valueIndex = currentAnswers.indexOf(button.dataset.value);
                if (valueIndex !== -1) {
                    currentAnswers.splice(valueIndex, 1);
                    this.removeButtonSelectedClasses(button);
                } else {
                    currentAnswers.push(button.dataset.value);
                    this.addButtonSelectedClasses(button);
                }
                this.formData.answers[stepIndex] = currentAnswers;
            }
        } else {
            // For single choice, fjern alle tidligere valg og sett kun ett svar
            stepPanel.querySelectorAll('.option-button').forEach(btn => {
                this.removeButtonSelectedClasses(btn);
            });

            this.addButtonSelectedClasses(button);
            this.formData.answers[stepIndex] = [button.dataset.value];
        }

        this.hideError();
    }

    handleTabKey(e) {
        if (e.key !== 'Tab') return;

        const focusableElements = this.getFocusableElements();
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }
    }

    showServiceDetails() {
        const serviceId = this.formData.selectedService;
        const recommendationContainer = this.modal.querySelector('.recommendation-container');
        //hide navigations
        this.prevButton.style.display = 'none';
        this.nextButton.style.display = 'none';
        this.progressIndicator.style.display = 'none';
        
        let hasSetService = false;

        recommendationContainer.querySelectorAll('.service-details-content').forEach(servicedetails => {
            const epiContentId = servicedetails.dataset.epiContentid?.trim()
            const service = serviceId === parseInt(epiContentId);

            if (service && !hasSetService) {
                servicedetails.style.display = 'grid';
                hasSetService = true;
            } else {
                servicedetails.style.display = 'none';
            }
        });
    }

    getFocusableElements() {
        const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        const elements = this.modal.querySelectorAll(focusableSelectors);
        return Array.from(elements).filter(el => {
            return !el.hasAttribute('disabled') &&
                !el.getAttribute('aria-hidden') &&
                el.offsetParent !== null;
        });
    }
    
    addButtonSelectedClasses(button) {
        button.classList.add('selected');
        button.classList.add('btn-primary');
        button.classList.remove('btn-tertiary');
        button.setAttribute('aria-checked', 'true');
    }
    
    removeButtonSelectedClasses(button) {
        button.classList.remove('selected');
        button.classList.remove('btn-primary');
        button.classList.add('btn-tertiary');
        button.setAttribute('aria-checked', 'false');
    }

    openModal() {
        this.lastFocusedElement = document.activeElement;

        const currentStep = this.stepHandler.getCurrentStep();
        
        this.stepPanels.forEach((panel, index) => {
            if (index === currentStep - 1) {
                panel.style.display = 'flex';
                panel.classList.add('active');
            } else {
                panel.style.display = 'none';
                panel.classList.remove('active');
            }
        });

        this.modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';

        requestAnimationFrame(() => {
            this.modal.classList.add('visible');
            setTimeout(() => {
                const focusableElements = this.getFocusableElements();
                if (focusableElements.length) {
                    focusableElements[0].focus();
                }
            }, 100);
        });
    }

    closeModal(resetForm = false) {
        this.modal.classList.remove('visible');
        document.body.style.overflow = '';
        
        if (resetForm) {
            this.resetForm();
            this.stepHandler.showStep(1)
        }

        setTimeout(() => {
            this.modal.style.display = 'none';
            if (this.lastFocusedElement) {
                this.lastFocusedElement.focus();
            }
        }, 300);
    }
    
    resetForm() {
        this.formData = {
            answers: new Array(this.options.informationQuestionCount).fill(null),
            priorityList: [],
            servicesClicked: [],
            postalCode: null,
            selectedService: null,
            phoneOrEmailInput: null,
            newsletter: false,
            terms: false,
            pageId: this.options.pageId
        };
        
        this.stepPanels.forEach(panel => {
            panel.querySelectorAll('.option-button').forEach(button => {
                this.removeButtonSelectedClasses(button);
            });
        });

        this.stepPanels.forEach(panel => {
            panel.querySelectorAll('.service-option').forEach(button => {
                this.removeButtonSelectedClasses(button);
            });
        });


        this.postalInput.value = '';
        this.phoneOrEmailInput.value = '';
        this.newsletterCheckbox.checked = false;
        this.termsCheckbox.checked = false;

        this.prevButton.style.display = 'block';
        this.nextButton.style.display = 'block';
        this.progressIndicator.style.display = 'block';

        this.modal.querySelectorAll('.service-guide-submit').forEach(button => {
            button.disabled = false;
        });

        // this.stepHandler.showStep(1);
        
    }
    
    submitForm() {
        const isValid = this.validateCurrentStep();
        
        if (!isValid) {
            return;
        }
        
        const formData = this.formData;
        const informationQuestions = this.options.informationQuestions;
        const reCaptchaSiteKey = document.getElementById('ReCaptchaSiteKey').value;

        this.modal.querySelectorAll('.service-guide-submit').forEach(button => {
            button.disabled = true;
        });
        
        //replace the answers with the question/answer pairs
        formData.answers = informationQuestions.map((question, index) => {
            return {
                question: question,
                answer: formData.answers[index]
            };
        });
        
        const _this = this;
        
        grecaptcha.ready(function() {
            grecaptcha.execute(reCaptchaSiteKey, {action: 'submit'}).then(function(token) {
                
                formData.gRecaptchaResponse = token;
                                
                fetch("/api/serviceguide/serviceguide-form", {
                    method: "POST",
                    body: JSON.stringify(formData),
                    headers: {
                        "Content-Type": "application/json",
                        "RequestVerificationToken": _this.modal.querySelector('input[name="__RequestVerificationToken"]').value
                    }
                }).then(
                    response => {
                        
                        if (response.ok) {
                            analyticsManager.trackServiceGuideSubmission(formData);

                            //next step
                            _this.stepHandler.navigateNext();
                            _this.modal.querySelectorAll('.service-guide-submit').forEach(button => {
                                button.style.display = 'none';
                            });

                            return false;

                        } else {
                            alert('Error submitting form');
                            _this.modal.querySelectorAll('.service-guide-submit').forEach(button => {
                                button.disabled = false;
                            });
                        }
                    }
                );
            });
        });
    }
}